/**
 * @file Custom print checkout state hook.
 *
 * Note vmasto: This is mainly an experiment for quick and easy state management
 * using hooks and the new context API. I'm not entirely sure if this is the right
 * direction and where it should belong so treat with caution and see it for what it is
 * (an experiment). This pattern should not generally be used in isolation like here
 * until we're clear this is a good abstraction that can serve us well.
 *
 * Big drawback of course with this is that it re-renders the entire sub-tree on
 * every state update (unlike Redux for example which bails out unnecessary re-renders)
 * so it should be used in isolation and small / non-expensive trees (if at all used).
 */

import React, { createContext, useContext, useReducer } from 'react';
import PropTypes from 'prop-types';

// Action Types
const SET_IDENTITY = 'SET_IDENTITY';

// Initial State
const initialState = {
  activeIdentityId: '',
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case SET_IDENTITY: {
      return {
        ...state,
        activeIdentityId: action.identityId,
      };
    }

    default:
      return state;
  }
};

const useContextState = (defaultState = {}) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    ...defaultState,
  });

  return {
    state,
    dispatch,
    setActiveIdentityId: identityId =>
      dispatch({
        type: SET_IDENTITY,
        identityId,
      }),
  };
};

export const CustomPrintCheckoutContext = createContext(null);

// Provider component
export const CustomPrintCheckoutStateProvider = ({
  children,
  defaultState,
}) => (
  <CustomPrintCheckoutContext.Provider value={useContextState(defaultState)}>
    {children}
  </CustomPrintCheckoutContext.Provider>
);

CustomPrintCheckoutStateProvider.propTypes = {
  children: PropTypes.node.isRequired,
  defaultState: PropTypes.object,
};

const useCustomPrintCheckoutState = () =>
  useContext(CustomPrintCheckoutContext);

export default useCustomPrintCheckoutState;
